import { createAction } from "@reduxjs/toolkit";
import { getCookie, removeCookie } from "../utils/cookieUtilities";
import { decodeJWT } from "../utils/generalUtils";

class CounterMap {
    #map = new Map();
    #id = crypto.randomUUID();
    /**
     * Increment the counter for the given key.
     * If the key doesn't exist yet, initialize it to 1.
     */
    inc(key) {
        const newValue = (this.#map.get(key) || 0) + 1;
        this.#map.set(key, newValue);
        return newValue;
    }

    /**
     * Decrement the counter for the given function key.
     * If the key doesn't exist yet, initialize it to -1.
     */
    dec(key) {
        const value = this.#map.get(key);
        if (value === undefined) {
            return null;
        }
        if (value === 0) {
            throw new Error("invalid dec of key " + key + " resulting in a negative count");
        }
        if (value === 1) {
            this.#map.delete(key);
            return 0;
        }
        const newValue = value - 1;
        this.#map.set(key, newValue);
        return newValue;
    }

    /**
     * Get the current value for a function key.
     * Returns 0 if the key doesn't exist.
     */
    get(key) {
        return this.#map.get(key) || 0;
    }

    /**
     * Set a specific value for a function key.
     */
    set(key, value) {
        if (value < 0) {
            throw new Error("Invalid set of key " + key + " to a negative value");
        }

        this.#map.set(key, value);
    }

    has(key) {
        return this.#map.has(key);
    }

    /**
     * Remove a function key entirely.
     */
    delete(key) {
        return this.#map.delete(key);
    }

    /**
     * Clear all stored function counters.
     */
    clear() {
        this.#map.clear();
    }

    /**
     * Iterate over the entries.
     */
    entries() {
        return this.#map.entries();
    }
}

export const fetchedOnAuth = new Map();
export const fetchedOnReady = new Map();
export const mountedInstances = new CounterMap();
export const unlisten = new Map();

export const logout = createAction('user/logout');
export const login = createAction('user/login');

export const initAuthState = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') ?? getCookie('token') ?? sessionStorage.getItem('token');
    const refreshToken = params.get('refreshToken') ?? getCookie('refreshToken') ?? sessionStorage.getItem('refreshToken');
    return { token, refreshToken };
}

export const signIn = (App, dispatch, navigate, token, refreshToken) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('refreshToken', refreshToken);
    App.emit?.('login', { token, username: decodeJWT(token)?.username, host: window.location.origin });
    dispatch(login({ token, refreshToken }));
    const params = new URLSearchParams(window.location.search);
    if (params.get('target')) {
        navigate?.(params.get('target'));
        params.delete('target');
    }
}
export const signOut = async (App, dispatch, navigate) => {
    const token = sessionStorage.getItem('token');
    sessionStorage.clear();
    removeCookie('token');
    removeCookie('refreshToken');
    App.emit?.('LOGOUT');
    App.emit?.('logout', { jwt: token, username: token ? decodeJWT(token)?.username : null })
    resetInit();
    dispatch(logout());
    navigate?.('/')
}

export const resetInit = () => {
    fetchedOnAuth.clear();
    fetchedOnReady.clear();
    mountedInstances.clear();
    unlisten.clear();
}